import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Divider } from "@mui/material";
import LoadingContainer from "../LoadingContainer/LoadingContainer";
import { useSuccessAlert } from "../../context/SuccessAlertContext/SuccessAlertContext";
import api_endpoints from "../../api";

import PageList from "./components/PageList";
import LocaleSelector from "./components/LocaleSelector";
import TranslationForm from "./components/TranslationForm";
import AddPageDialog from "./components/AddPageDialog";

export default function PlatformView({ platform, refreshKey = 0 }) {
  const { setShowSuccessAlert, setMessageSuccessAlert } = useSuccessAlert();

  // pages
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [loadingPagesError, setLoadingPagesError] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  // locales
  const localesList = useRef([]); // full objects
  const [localesNameList, setLocalesNameList] = useState([]); // strings
  const [frequentLocales, setFrequentLocales] = useState([]);
  const [selectedLocale, setSelectedLocale] = useState(null);

  // reference + current translations
  const [referenceTranslation, setReferenceTranslation] = useState(null);
  const [formValues, setFormValues] = useState({});
  const baseKeys = useMemo(() => selectedPage?.texts || [], [selectedPage]);

  // loading states
  const [loadingPageData, setLoadingPageData] = useState(true);
  const [loadingPageDataError, setLoadingPageDataError] = useState(false);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const [loadingTranslationsError, setLoadingTranslationsError] = useState(false);

  // dialog
  const [addOpen, setAddOpen] = useState(false);

  const loadPages = useCallback(async () => {
    setLoadingPages(true);
    setLoadingPagesError(false);
    try {
      const resp = await api_endpoints.getPages(platform);
      if (resp.status === 200) setPages(resp.data);
    } catch {
      setLoadingPagesError(true);
    } finally {
      setLoadingPages(false);
    }
  }, [platform]);

  // ------- fetch pages on platform change -------
  useEffect(() => {
    loadPages();

    // reset selections
    setSelectedPage(null);
    setSelectedLocale(null);
    setReferenceTranslation(null);
    setFormValues({});
    setLoadingPageData(true);
    setLoadingPageDataError(false);
    setLoadingTranslations(false);
    setLoadingTranslationsError(false);
  }, [loadPages, refreshKey]);

  // ------- when page changes, load locales + english ref -------
  useEffect(() => {
    if (!selectedPage) return;

    (async () => {
      setLoadingPageData(true);
      setLoadingPageDataError(false);
      try {
        const [allResp, frequentResp, refResp] = await Promise.all([
          api_endpoints.getAllLocales(),
          api_endpoints.getFrequentLocales(10),
          api_endpoints.getPageTranslation("en_US", selectedPage.page_name, platform),
        ]);

        if (allResp.status === 200) {
          localesList.current = allResp.data.locales;
          setLocalesNameList(allResp.data.locales.map((l) => l.locale));
        }
        if (frequentResp.status === 200) {
          setFrequentLocales(frequentResp.data.locales.map((l) => l.locale));
        }
        if (refResp.status === 200) {
          setReferenceTranslation(refResp.data?.translations || {});
        }
      } catch {
        setLoadingPageDataError(true);
      } finally {
        setLoadingPageData(false);
      }
    })();

    // clear locale-specific until user picks one
    setSelectedLocale(null);
    setFormValues({});
    setLoadingTranslations(false);
    setLoadingTranslationsError(false);
  }, [selectedPage, platform]);

  // ------- when locale changes, load its translations -------
  useEffect(() => {
    if (!selectedPage || !selectedLocale) return;

    (async () => {
      setLoadingTranslations(true);
      setLoadingTranslationsError(false);
      try {
        const locale = localesList.current.find((l) => l.locale === selectedLocale);
        if (!locale) throw new Error("Locale not found");

        const resp = await api_endpoints.getPageTranslation(
          locale.locale_abbreviation,
          selectedPage.page_name,
          platform
        );

        const out = {};
        if (resp.status === 200) {
          for (const k of selectedPage.texts) {
            out[k] = resp.data?.translations?.[k] ?? "";
          }
        } else {
          for (const k of selectedPage.texts) out[k] = "";
        }
        setFormValues(out);
      } catch {
        setLoadingTranslationsError(true);
      } finally {
        setLoadingTranslations(false);
      }
    })();
  }, [selectedLocale, selectedPage, platform]);

  // ------- form handlers (controlled, no remounts) -------
  const onFormChange = useCallback((key, val) => {
    setFormValues((prev) => (prev[key] === val ? prev : { ...prev, [key]: val }));
  }, []);

  const onRemoveExtraKey = useCallback(
    (key) => {
      if (baseKeys.includes(key)) return;
      setFormValues(({ [key]: _omit, ...rest }) => rest);
    },
    [baseKeys]
  );

  const onSubmit = useCallback(async (values) => {
    if (!selectedPage || !selectedLocale) return;

    try {
      const locale = localesList.current.find((l) => l.locale === selectedLocale);
      if (!locale) return;

      // persist any new keys first
      const newKeys = Object.keys(values).filter((k) => !baseKeys.includes(k));
      if (newKeys.length) {
        await api_endpoints.addPageTexts(selectedPage._id, newKeys);
        setSelectedPage((prev) => ({ ...prev, texts: [...prev.texts, ...newKeys] }));
      }

      const resp = await api_endpoints.editTranslations(selectedPage._id, locale._id, values);
      if (resp.status === 200) {
        setMessageSuccessAlert(resp.data);
        setShowSuccessAlert(true);
      }
    } catch {
      /* surface a toast if you want */
    }
  }, [baseKeys, selectedLocale, selectedPage, setMessageSuccessAlert, setShowSuccessAlert]);

  // ------- create page -------
  const onCreatePage = useCallback(async (payload) => {
    const res = await api_endpoints.addPage(payload);
    if (res.status === 201) {
      const created = res.data;
      setPages((prev) => [created, ...prev]);
      setSelectedPage(created);
      setAddOpen(false);
    }
  }, []);

  return (
    <LoadingContainer loading={loadingPages} error={loadingPagesError}>
      <div className="flex flex-1">
        <PageList
          pages={pages}
          selectedPage={selectedPage}
          onSelect={setSelectedPage}
          onAddPageOpen={() => setAddOpen(true)}
        />
        <Divider flexItem orientation="vertical" />
        {selectedPage ? (
          <LoadingContainer loading={loadingPageData} error={loadingPageDataError}>
            <div className="flex flex-1 m-2 [&>div]:flex [&>div]:flex-1 [&>div]:flex-col [&>div]:space-y-4">
              <div>
                <LocaleSelector
                  localesNameList={localesNameList}
                  frequentLocales={frequentLocales}
                  selectedLocale={selectedLocale}
                  setSelectedLocale={setSelectedLocale}
                />
                {!selectedLocale ? (
                  <div className="mt-3">Sélectionner une langue pour affecter la traduction</div>
                ) : (
                  <LoadingContainer loading={loadingTranslations} error={loadingTranslationsError}>
                    <TranslationForm
                      baseKeys={baseKeys}
                      values={formValues}
                      onChange={onFormChange}
                      onRemoveExtraKey={onRemoveExtraKey}
                      onSubmit={onSubmit}
                      referenceTranslations={referenceTranslation}
                      busy={loadingTranslations}
                    />
                  </LoadingContainer>
                )}
              </div>
            </div>
          </LoadingContainer>
        ) : (
          <div className="flex flex-1 m-4">Sélectionner une page pour visionner ses champs</div>
        )}
      </div>

      <AddPageDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={onCreatePage}
        initialPlatform={platform}
      />
    </LoadingContainer>
  );
}
